// BMG Theme JS — Bootstrap + Dark Mode + Scroll Reveal

/**
 * BMG Theme JavaScript
 *
 * Includes Bootstrap 5 and custom functionality.
 * Refined luxury editorial aesthetic.
 */

// Import Bootstrap
import 'bootstrap';

/**
 * Skip Link Focus Fix
 * Helps with accessibility for keyboard only users.
 */
( function() {
	const isIe = /(trident|msie)/i.test( navigator.userAgent );

	if ( isIe && document.getElementById && window.addEventListener ) {
		window.addEventListener(
			'hashchange',
			function() {
				const id = location.hash.substring( 1 );
				let element;

				if ( ! /^[A-z0-9_-]+$/.test( id ) ) {
					return;
				}

				element = document.getElementById( id );

				if ( element ) {
					if ( ! /^(?:a|select|input|button|textarea)$/i.test( element.tagName ) ) {
						element.tabIndex = -1;
					}

					element.focus();
				}
			},
			false
		);
	}
} )();

/**
 * Dark Mode Toggle
 * Handles theme switching, localStorage persistence, and system preference detection.
 */
( function() {
	'use strict';

	const STORAGE_KEY = 'bmg-theme-mode';
	const THEME_ATTRIBUTE = 'data-bs-theme';

	function getSystemPreference() {
		if ( window.matchMedia && window.matchMedia( '(prefers-color-scheme: dark)' ).matches ) {
			return 'dark';
		}
		return 'light';
	}

	function getStoredTheme() {
		try {
			return localStorage.getItem( STORAGE_KEY );
		} catch ( e ) {
			return null;
		}
	}

	function setStoredTheme( theme ) {
		try {
			localStorage.setItem( STORAGE_KEY, theme );
		} catch ( e ) {
			// localStorage not available
		}
	}

	function applyTheme( theme ) {
		document.documentElement.setAttribute( THEME_ATTRIBUTE, theme );
		updateToggleButtons( theme );
	}

	function getCurrentTheme() {
		const storedTheme = getStoredTheme();
		if ( storedTheme ) {
			return storedTheme;
		}
		return getSystemPreference();
	}

	function toggleTheme() {
		const currentTheme = document.documentElement.getAttribute( THEME_ATTRIBUTE ) || 'light';
		const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
		applyTheme( newTheme );
		setStoredTheme( newTheme );
	}

	function updateToggleButtons( theme ) {
		const buttons = document.querySelectorAll( '.dark-mode-toggle' );
		buttons.forEach( function( button ) {
			const lightLabel = button.getAttribute( 'data-light-label' ) || 'Switch to dark mode';
			const darkLabel = button.getAttribute( 'data-dark-label' ) || 'Switch to light mode';
			button.setAttribute( 'aria-label', theme === 'dark' ? darkLabel : lightLabel );
		} );
	}

	function init() {
		const theme = getCurrentTheme();
		applyTheme( theme );

		document.querySelectorAll( '.dark-mode-toggle' ).forEach( function( button ) {
			button.addEventListener( 'click', toggleTheme );
		} );

		if ( window.matchMedia ) {
			const mediaQuery = window.matchMedia( '(prefers-color-scheme: dark)' );
			mediaQuery.addEventListener( 'change', function( e ) {
				if ( ! getStoredTheme() ) {
					applyTheme( e.matches ? 'dark' : 'light' );
				}
			} );
		}
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * BMG Reveal System
 * Uses IntersectionObserver to reveal elements with .bmg-reveal class.
 * Adds .is-visible when element enters viewport at 15% threshold.
 * One-way — once revealed, stays revealed.
 */
( function() {
	'use strict';

	function init() {
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		const reveals = document.querySelectorAll( '.bmg-reveal' );

		if ( ! reveals.length ) {
			return;
		}

		if ( prefersReducedMotion || ! ( 'IntersectionObserver' in window ) ) {
			reveals.forEach( function( el ) {
				el.classList.add( 'is-visible' );
			} );
			return;
		}

		const observer = new IntersectionObserver( function( entries ) {
			entries.forEach( function( entry ) {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'is-visible' );
					observer.unobserve( entry.target );
				}
			} );
		}, { threshold: 0.15 } );

		reveals.forEach( function( el ) {
			observer.observe( el );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Smooth Scroll
 * Handles smooth scrolling for anchor links with header offset.
 */
( function() {
	'use strict';

	function init() {
		document.querySelectorAll( 'a[href^="#"]' ).forEach( function( anchor ) {
			anchor.addEventListener( 'click', function( e ) {
				const targetId = this.getAttribute( 'href' );

				if ( targetId === '#' ) {
					return;
				}

				const target = document.querySelector( targetId );

				if ( target ) {
					e.preventDefault();
					const headerOffset = 80;
					const elementPosition = target.getBoundingClientRect().top;
					const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

					window.scrollTo( {
						top: offsetPosition,
						behavior: 'smooth'
					} );
				}
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Smart Sticky Header
 * Single scroll listener via rAF loop.
 *
 * Full transparent in hero, hidden on scroll-down past hero,
 * condensed on scroll-up. Inner pages without hero start condensed.
 */
( function() {
	'use strict';

	var lastScrollY = 0;
	var ticking = false;
	var SCROLL_THRESHOLD = 10;

	function init() {
		var navbar = document.getElementById( 'main-nav' );

		if ( ! navbar ) {
			return;
		}

		var heroSection = document.querySelector( '.mp-hero' );

		// Inner pages without a hero — start condensed immediately
		if ( ! heroSection ) {
			navbar.classList.add( 'is-condensed' );
		}

		function getHeroBottom() {
			if ( ! heroSection ) {
				return 0;
			}
			return heroSection.offsetTop + heroSection.offsetHeight;
		}

		function update() {
			var currentScrollY = window.scrollY;
			var delta = currentScrollY - lastScrollY;
			var pastHero = currentScrollY > getHeroBottom();

			// Sticky header — only updates on meaningful scroll deltas
			if ( Math.abs( delta ) >= SCROLL_THRESHOLD ) {
				if ( ! pastHero ) {
					// In hero — full transparent navbar
					navbar.classList.remove( 'is-hidden', 'is-condensed' );
				} else if ( delta > 0 ) {
					// Scrolling DOWN past hero — hide
					navbar.classList.add( 'is-hidden' );
					navbar.classList.remove( 'is-condensed' );
				} else {
					// Scrolling UP past hero — show condensed
					navbar.classList.remove( 'is-hidden' );
					navbar.classList.add( 'is-condensed' );
				}

				lastScrollY = currentScrollY;
			}

			ticking = false;
		}

		function onScroll() {
			if ( ! ticking ) {
				requestAnimationFrame( update );
				ticking = true;
			}
		}

		lastScrollY = window.scrollY;
		window.addEventListener( 'scroll', onScroll, { passive: true } );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Gallery Lightbox with Prev/Next Navigation
 * Opens gallery images in a full-screen overlay.
 * Supports keyboard navigation (arrows, Escape).
 * Works with both homepage before/after gallery and gallery page.
 */
( function() {
	'use strict';

	function init() {
		var lightbox = document.getElementById( 'mp-lightbox' );

		if ( ! lightbox ) {
			return;
		}

		var lightboxImg = lightbox.querySelector( '.mp-lightbox__img' );
		var closeBtn = lightbox.querySelector( '.mp-lightbox__close' );
		var prevBtn = lightbox.querySelector( '.mp-lightbox__prev' );
		var nextBtn = lightbox.querySelector( '.mp-lightbox__next' );
		var galleryImages = [];
		var currentIndex = -1;

		function collectImages() {
			galleryImages = Array.prototype.slice.call(
				document.querySelectorAll( '.mp-gallery-img' )
			);
		}

		function getFullSrc( img ) {
			return img.getAttribute( 'data-full' ) || img.src;
		}

		function showImage( index ) {
			if ( index < 0 || index >= galleryImages.length ) {
				return;
			}
			currentIndex = index;
			var img = galleryImages[ currentIndex ];
			lightboxImg.style.opacity = '0';
			lightboxImg.src = getFullSrc( img );
			lightboxImg.alt = img.alt || '';
			// Fade in after src is set
			setTimeout( function() {
				lightboxImg.style.opacity = '1';
			}, 50 );
			updateNavButtons();
		}

		function updateNavButtons() {
			if ( ! prevBtn || ! nextBtn ) {
				return;
			}
			var hasMultiple = galleryImages.length > 1;
			prevBtn.style.display = hasMultiple ? '' : 'none';
			nextBtn.style.display = hasMultiple ? '' : 'none';
		}

		function openLightbox( img ) {
			collectImages();
			currentIndex = galleryImages.indexOf( img );
			if ( currentIndex === -1 ) {
				currentIndex = 0;
			}
			lightboxImg.src = getFullSrc( img );
			lightboxImg.alt = img.alt || '';
			lightboxImg.style.opacity = '1';
			lightbox.classList.add( 'is-active' );
			lightbox.setAttribute( 'aria-hidden', 'false' );
			document.body.style.overflow = 'hidden';
			updateNavButtons();
		}

		function closeLightbox() {
			lightbox.classList.remove( 'is-active' );
			lightbox.setAttribute( 'aria-hidden', 'true' );
			document.body.style.overflow = '';
			currentIndex = -1;
			setTimeout( function() {
				if ( ! lightbox.classList.contains( 'is-active' ) ) {
					lightboxImg.src = '';
				}
			}, 300 );
		}

		function prevImage() {
			if ( currentIndex > 0 ) {
				showImage( currentIndex - 1 );
			} else if ( galleryImages.length > 1 ) {
				showImage( galleryImages.length - 1 );
			}
		}

		function nextImage() {
			if ( currentIndex < galleryImages.length - 1 ) {
				showImage( currentIndex + 1 );
			} else if ( galleryImages.length > 1 ) {
				showImage( 0 );
			}
		}

		// Event delegation — clicks on .mp-gallery-img
		document.addEventListener( 'click', function( e ) {
			var img = e.target.closest( '.mp-gallery-img' );
			if ( img ) {
				e.preventDefault();
				openLightbox( img );
			}
		} );

		closeBtn.addEventListener( 'click', closeLightbox );

		if ( prevBtn ) {
			prevBtn.addEventListener( 'click', prevImage );
		}
		if ( nextBtn ) {
			nextBtn.addEventListener( 'click', nextImage );
		}

		lightbox.addEventListener( 'click', function( e ) {
			if ( e.target === lightbox ) {
				closeLightbox();
			}
		} );

		document.addEventListener( 'keydown', function( e ) {
			if ( ! lightbox.classList.contains( 'is-active' ) ) {
				return;
			}
			if ( e.key === 'Escape' ) {
				closeLightbox();
			} else if ( e.key === 'ArrowLeft' ) {
				prevImage();
			} else if ( e.key === 'ArrowRight' ) {
				nextImage();
			}
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Promotional Popup
 * Shows once per session after a configurable delay.
 * Close on X button, overlay click, or Escape key.
 */
( function() {
	'use strict';

	function init() {
		var popup = document.getElementById( 'mp-popup' );

		if ( ! popup ) {
			return;
		}

		// Only show once per session.
		try {
			if ( sessionStorage.getItem( 'mp_popup_shown' ) ) {
				return;
			}
		} catch ( e ) {
			// sessionStorage not available — proceed anyway.
		}

		var delay = parseInt( popup.getAttribute( 'data-delay' ), 10 ) || 5;
		var closeBtn = popup.querySelector( '.mp-popup__close' );

		function showPopup() {
			popup.classList.add( 'is-active' );
			popup.setAttribute( 'aria-hidden', 'false' );
			document.body.style.overflow = 'hidden';
		}

		function closePopup() {
			popup.classList.add( 'is-closing' );
			popup.classList.remove( 'is-active' );
			document.body.style.overflow = '';

			try {
				sessionStorage.setItem( 'mp_popup_shown', 'true' );
			} catch ( e ) {
				// Ignore.
			}

			setTimeout( function() {
				popup.setAttribute( 'aria-hidden', 'true' );
				popup.classList.remove( 'is-closing' );
			}, 300 );
		}

		// Show after delay.
		setTimeout( showPopup, delay * 1000 );

		// Close handlers.
		closeBtn.addEventListener( 'click', closePopup );

		popup.addEventListener( 'click', function( e ) {
			if ( e.target === popup ) {
				closePopup();
			}
		} );

		document.addEventListener( 'keydown', function( e ) {
			if ( e.key === 'Escape' && popup.classList.contains( 'is-active' ) ) {
				closePopup();
			}
		} );

		// Close if CTA is clicked (navigating away).
		var cta = popup.querySelector( '.mp-popup__cta' );
		if ( cta ) {
			cta.addEventListener( 'click', closePopup );
		}
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Testimonials Carousel
 * Vanilla scroll-snap carousel. Prev/next buttons scroll the track by one
 * slide width. Buttons disable themselves at the start/end of the track.
 */
( function() {
	'use strict';

	function initCarousel( root ) {
		var track   = root.querySelector( '[data-carousel-track]' );
		var prevBtn = root.querySelector( '[data-carousel-prev]' );
		var nextBtn = root.querySelector( '[data-carousel-next]' );

		if ( ! track || ! prevBtn || ! nextBtn ) {
			return;
		}

		function getStep() {
			var firstSlide = track.firstElementChild;
			if ( ! firstSlide ) {
				return track.clientWidth;
			}
			var slideWidth = firstSlide.getBoundingClientRect().width;
			var styles     = window.getComputedStyle( track );
			var gap        = parseFloat( styles.columnGap || styles.gap || 0 ) || 0;
			return slideWidth + gap;
		}

		function updateButtons() {
			var maxScroll = track.scrollWidth - track.clientWidth;
			// 1px tolerance for sub-pixel rounding
			prevBtn.disabled = track.scrollLeft <= 1;
			nextBtn.disabled = track.scrollLeft >= ( maxScroll - 1 );
		}

		prevBtn.addEventListener( 'click', function() {
			track.scrollBy( { left: -getStep(), behavior: 'smooth' } );
		} );

		nextBtn.addEventListener( 'click', function() {
			track.scrollBy( { left: getStep(), behavior: 'smooth' } );
		} );

		track.addEventListener( 'scroll', function() {
			window.requestAnimationFrame( updateButtons );
		}, { passive: true } );

		window.addEventListener( 'resize', updateButtons );

		updateButtons();
	}

	function init() {
		var carousels = document.querySelectorAll( '[data-testimonials-carousel]' );
		Array.prototype.forEach.call( carousels, initCarousel );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Back to Top Button
 * Shows a fixed button after scrolling 2x viewport height.
 * Smooth scrolls to top on click. Respects prefers-reduced-motion.
 */
( function() {
	'use strict';

	function init() {
		var backToTop = document.querySelector( '.mp-back-to-top' );

		if ( ! backToTop ) {
			return;
		}

		var threshold = window.innerHeight * 2;
		var prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		window.addEventListener( 'scroll', function() {
			if ( window.scrollY > threshold ) {
				backToTop.classList.add( 'is-visible' );
			} else {
				backToTop.classList.remove( 'is-visible' );
			}
		}, { passive: true } );

		backToTop.addEventListener( 'click', function() {
			window.scrollTo( {
				top: 0,
				behavior: prefersReducedMotion ? 'auto' : 'smooth'
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
